import { useEffect, useRef, useState } from "react"
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  LinearProgress,
  Stack,
  Typography,
} from "@mui/material"
import { Close } from "@mui/icons-material"
import { enqueueSnackbar } from "notistack"
import { store } from "@core/store"
import { ContentBinding, ContentOwner } from "../../interfaces"
import { useAdvWriteAccess } from "../../hooks/useAdvWriteAccess"
import {
  contentsApi,
  useCreateBindingMutation,
  useDeleteBindingMutation,
  useGetContentBindingsQuery,
  useGetContentQuery,
} from "../../store/contentsApi"
import {
  contentErrorMessage,
  normalizeContentError,
} from "../../utils/contentErrors"
import { removeContent } from "../../services/assetLinkCache"
import { formatContentDateTime } from "../../utils/formatContentDateTime"
import { AssetPreview } from "./assetPreview"
import { UrlButtonsPreview } from "./urlButtonsPreview"
import { ContentBindingSelector } from "./contentBindingSelector"

export interface ContentViewerDialogProps {
  contentId: number
  open: boolean
  onClose: () => void
  readOnly?: boolean
}
const isNotFound = (error: unknown, code: string) => {
  const shape = normalizeContentError(error)
  return shape.status === 404 && shape.code === code
}
const bindingOwner = (binding: ContentBinding) =>
  binding.order_id !== null
    ? { order_id: binding.order_id }
    : { campaign_id: binding.campaign_id! }
const bindingName = (binding: ContentBinding) =>
  binding.order_id !== null
    ? binding.order_name || `Ордер #${binding.order_id}`
    : binding.campaign_name || `Кампания #${binding.campaign_id}`

// Отдельный сеанс на каждый ID: локальные формы и ошибки не переходят к другому материалу.
export const ContentViewerDialog = (props: ContentViewerDialogProps) =>
  props.open && Number.isSafeInteger(props.contentId) && props.contentId > 0 ? (
    <ContentViewerSession key={props.contentId} {...props} />
  ) : null

const ContentViewerSession = ({
  contentId,
  onClose,
  readOnly,
}: ContentViewerDialogProps) => {
  const canWrite = useAdvWriteAccess() && !readOnly
  const [missing, setMissing] = useState(false)
  const [selectorOpen, setSelectorOpen] = useState(false)
  const [pending, setPending] = useState(false)
  const [mutationError, setMutationError] = useState<string | null>(null)
  const lock = useRef(false)
  const live = useRef(true)
  const invalidatedMissing = useRef(false)
  const args = { pathParams: { contentId } }
  const content = useGetContentQuery(args, {
    refetchOnMountOrArgChange: true,
  })
  const bindings = useGetContentBindingsQuery(args, {
    refetchOnMountOrArgChange: true,
  })
  const [createBinding] = useCreateBindingMutation()
  const [deleteBinding] = useDeleteBindingMutation()
  const current = !missing && !content.isError ? content.currentData : undefined

  useEffect(() => {
    live.current = true
    return () => {
      live.current = false
    }
  }, [])
  useEffect(() => {
    if (
      (!content.isFetching && isNotFound(content.error, "content_not_found")) ||
      (!bindings.isFetching && isNotFound(bindings.error, "content_not_found"))
    )
      setMissing(true)
  }, [content.error, content.isFetching, bindings.error, bindings.isFetching])
  useEffect(() => {
    if (!missing || invalidatedMissing.current) return
    invalidatedMissing.current = true
    removeContent(contentId)
    store.dispatch(
      contentsApi.util.invalidateTags([
        { type: "Contents", id: contentId },
        { type: "Contents", id: "LIST" },
      ]),
    )
  }, [missing, contentId])

  const close = () => {
    if (!lock.current) onClose()
  }
  const runMutation = async (
    operation: () => Promise<{ message: string; variant: "success" | "info" }>,
  ) => {
    if (!canWrite || !current || lock.current) return
    lock.current = true
    setPending(true)
    setMutationError(null)
    try {
      const notification = await operation()
      enqueueSnackbar(notification.message, { variant: notification.variant })
      if (live.current) setSelectorOpen(false)
    } catch (error) {
      if (live.current) {
        setMutationError(contentErrorMessage(error))
        if (isNotFound(error, "content_not_found")) setMissing(true)
      }
    } finally {
      lock.current = false
      if (live.current) setPending(false)
    }
  }
  const bind = (owner: ContentOwner) =>
    void runMutation(async () => {
      const result = await createBinding({
        pathParams: { contentId },
        body:
          owner.kind === "order"
            ? { order_id: owner.id }
            : { campaign_id: owner.id },
      }).unwrap()
      return result.existing
        ? { message: "Материал уже был привязан", variant: "info" }
        : { message: "Материал привязан", variant: "success" }
    })
  const unbind = (binding: ContentBinding) =>
    void runMutation(async () => {
      try {
        await deleteBinding({
          pathParams: { contentId, bindingId: binding.id },
          body: bindingOwner(binding),
        }).unwrap()
        return {
          message: "Привязка снята (материал сохранён)",
          variant: "success",
        }
      } catch (error) {
        if (!isNotFound(error, "binding_not_found")) throw error
        return { message: "Привязка уже снята", variant: "info" }
      }
    })
  const retryContent = () => {
    if (missing) {
      invalidatedMissing.current = false
      setMissing(false)
      void bindings.refetch()
    }
    void content.refetch()
  }
  return (
    <>
      <Dialog
        open
        fullWidth
        maxWidth="md"
        onClose={close}
        disableEscapeKeyDown={pending}
      >
        <DialogTitle>
          Материал #{contentId}
          {!pending && (
            <IconButton
              aria-label="close"
              onClick={close}
              sx={{ position: "absolute", right: 8, top: 8 }}
            >
              <Close />
            </IconButton>
          )}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2}>
            {pending && <LinearProgress />}
            {mutationError && !selectorOpen && !missing && (
              <Alert severity="error">{mutationError}</Alert>
            )}
            {missing || content.isError ? (
              <Alert
                severity="error"
                action={
                  <Button
                    onClick={retryContent}
                    aria-label="Повторить загрузку материала"
                  >
                    Повторить
                  </Button>
                }
              >
                {missing
                  ? "Материал не найден — обновите список"
                  : "Не удалось загрузить материал"}
              </Alert>
            ) : !current ? (
              <LinearProgress />
            ) : (
              <>
                <Typography
                  component="div"
                  sx={{ whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}
                >
                  {current.text || "Без текста"}
                </Typography>
                <Typography variant="caption">
                  Создан: {formatContentDateTime(current.created_at)}
                </Typography>
                <Stack spacing={2}>
                  {current.assets.map((asset) => (
                    <AssetPreview
                      key={asset.id}
                      contentId={contentId}
                      asset={asset}
                    />
                  ))}
                </Stack>
                <UrlButtonsPreview markup={current.reply_markup} />
                <Typography variant="h6" component="h2">
                  Привязки
                </Typography>
                {bindings.isError ? (
                  <Alert
                    severity="error"
                    action={
                      <Button
                        onClick={() => bindings.refetch()}
                        aria-label="Повторить загрузку привязок"
                      >
                        Повторить
                      </Button>
                    }
                  >
                    Не удалось загрузить привязки
                  </Alert>
                ) : !bindings.currentData ? (
                  <LinearProgress />
                ) : bindings.currentData.length === 0 ? (
                  <Stack spacing={1}>
                    <Typography>Нет привязок</Typography>
                    <Typography color="text.secondary">
                      Материал ни к чему не привязан. Привяжите его к ордеру или
                      кампании
                    </Typography>
                  </Stack>
                ) : (
                  (["order", "campaign"] as const).map((kind) => {
                    const rows = bindings.currentData!.filter((binding) =>
                      kind === "order"
                        ? binding.order_id !== null
                        : binding.campaign_id !== null,
                    )
                    return (
                      rows.length > 0 && (
                        <Stack key={kind} spacing={1}>
                          <Typography variant="subtitle1" component="h3">
                            {kind === "order" ? "Ордера" : "Кампании"}
                          </Typography>
                          {rows.map((binding) => (
                            <Box
                              key={binding.id}
                              role="group"
                              aria-label={bindingName(binding)}
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                              }}
                            >
                              <Typography>{bindingName(binding)}</Typography>
                              {canWrite && (
                                <Button
                                  disabled={pending}
                                  onClick={() => unbind(binding)}
                                >
                                  Отвязать
                                </Button>
                              )}
                            </Box>
                          ))}
                        </Stack>
                      )
                    )
                  })
                )}
                {canWrite && (
                  <Button
                    disabled={pending}
                    onClick={() => {
                      setMutationError(null)
                      setSelectorOpen(true)
                    }}
                  >
                    Привязать…
                  </Button>
                )}
              </>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button disabled={pending} onClick={close}>
            Закрыть
          </Button>
        </DialogActions>
      </Dialog>
      {selectorOpen && canWrite && current && (
        <ContentBindingSelector
          pending={pending}
          error={mutationError}
          onClose={() => {
            if (!lock.current) setSelectorOpen(false)
          }}
          onSubmit={bind}
        />
      )}
    </>
  )
}
